export interface CaseStudy {
  slug: string;
  title: string;
  subtitle: string;
  category: 'work' | 'research';
  tags: string[];
  year: number;
  duration: string;
  role: string;
  team: string[];
  image: string;
  summary: string;
  problem: string;
  solution: string;
  impact: string[];
  process: {
    title: string;
    description: string;
    image?: string;
  }[];
  outcomes: {
    metric: string;
    value: string;
    description: string;
  }[];
  keyLearnings: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    slug: 'fintech-mobile-app',
    title: 'FinTech Mobile Banking',
    subtitle: 'Redesigning the digital banking experience for Gen Z users',
    category: 'work',
    tags: ['Mobile Design', 'User Research', 'Design System'],
    year: 2024,
    duration: '4 months',
    role: 'Senior Product Designer',
    team: ['2 Designers', '3 Engineers', '1 PM', '1 Researcher'],
    image: 'https://images.unsplash.com/photo-1678667720699-5c0fc04ac166?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2JpbGUlMjBhcHAlMjBkZXNpZ24lMjBtb2NrdXB8ZW58MXx8fHwxNzU3NTkzMjM5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    summary: 'Led the complete redesign of a mobile banking app, focusing on improving user engagement and reducing task completion time for young professionals.',
    problem: 'The existing banking app had a 32% bounce rate and users complained about complex navigation and outdated visual design. Young users found it difficult to understand their financial data and make informed decisions.',
    solution: 'Created a user-centered design system with simplified navigation, personalized insights, and gamified savings features. Implemented a card-based interface with clear visual hierarchy and microinteractions.',
    impact: [
      '45% reduction in task completion time',
      '60% increase in daily active users',
      '38% improvement in user satisfaction scores',
      '25% increase in savings goal completions'
    ],
    process: [
      {
        title: 'Discovery & Research',
        description: 'Conducted user interviews with 24 participants, competitive analysis, and stakeholder workshops to understand pain points and opportunities.'
      },
      {
        title: 'Information Architecture',
        description: 'Redesigned the app structure based on user mental models, creating intuitive navigation paths and reducing cognitive load.'
      },
      {
        title: 'Design System',
        description: 'Built a comprehensive design system with reusable components, ensuring consistency across all touchpoints.'
      },
      {
        title: 'Prototyping & Testing',
        description: 'Created high-fidelity prototypes and conducted usability testing with 18 users to validate design decisions.'
      },
      {
        title: 'Implementation',
        description: 'Worked closely with engineering team to ensure pixel-perfect implementation and smooth animations.'
      }
    ],
    outcomes: [
      {
        metric: 'Task Completion Time',
        value: '-45%',
        description: 'Average time to complete core banking tasks'
      },
      {
        metric: 'User Satisfaction',
        value: '4.7/5',
        description: 'App store rating after redesign'
      },
      {
        metric: 'Feature Adoption',
        value: '+73%',
        description: 'Increase in new feature usage'
      }
    ],
    keyLearnings: [
      'Young users prefer visual data representations over text-heavy interfaces',
      'Microinteractions significantly impact perceived app performance',
      'Personalization increases user engagement but must be implemented thoughtfully'
    ]
  },
  {
    slug: 'ai-productivity-tool',
    title: 'AI Productivity Assistant',
    subtitle: 'Building trust in AI through transparent and ethical design',
    category: 'work',
    tags: ['AI/ML', 'Dashboard Design', 'Enterprise UX'],
    year: 2024,
    duration: '6 months',
    role: 'Lead Product Designer',
    team: ['3 Designers', '5 Engineers', '2 PMs', '1 Data Scientist'],
    image: 'https://images.unsplash.com/photo-1675627452466-fec3fa89f394?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB1aSUyMGRlc2lnbiUyMHByb2Nlc3N8ZW58MXx8fHwxNzU3NjA1NDE4fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    summary: 'Designed an enterprise AI productivity tool that helps teams automate workflows while maintaining transparency and user control over AI decisions.',
    problem: 'Enterprise users were hesitant to adopt AI tools due to lack of transparency, fear of job replacement, and concerns about data privacy. Existing solutions felt like black boxes.',
    solution: 'Developed a transparent AI interface with explainable recommendations, user control mechanisms, and progressive disclosure of AI capabilities. Created trust through clear communication of AI limitations and benefits.',
    impact: [
      '89% user adoption rate within 3 months',
      '3.2 hours saved per user per week',
      '92% trust score in user surveys',
      '40% reduction in repetitive tasks'
    ],
    process: [
      {
        title: 'AI Ethics Research',
        description: 'Studied AI ethics frameworks and conducted workshops with stakeholders to establish design principles for responsible AI.'
      },
      {
        title: 'User Mental Models',
        description: 'Researched how users understand AI through card sorting exercises and mental model interviews.'
      },
      {
        title: 'Transparency Framework',
        description: 'Designed a system for explaining AI decisions and recommendations in human-understandable terms.'
      },
      {
        title: 'Progressive Disclosure',
        description: 'Created layered interfaces that reveal AI complexity based on user expertise and needs.'
      }
    ],
    outcomes: [
      {
        metric: 'User Trust',
        value: '92%',
        description: 'Users who trust AI recommendations'
      },
      {
        metric: 'Time Saved',
        value: '3.2hrs/week',
        description: 'Average time saved per user'
      },
      {
        metric: 'Feature Discoverability',
        value: '+156%',
        description: 'Improvement in feature discovery'
      }
    ],
    keyLearnings: [
      'Transparency builds trust more than accuracy alone',
      'Users need control mechanisms to feel comfortable with AI',
      'Progressive disclosure helps users learn AI capabilities gradually'
    ]
  },
  {
    slug: 'voice-interface-research',
    title: 'Voice Interface Accessibility',
    subtitle: 'Research study on voice UI accessibility for users with disabilities',
    category: 'research',
    tags: ['Voice UI', 'Accessibility', 'User Research'],
    year: 2023,
    duration: '8 months',
    role: 'UX Researcher',
    team: ['1 Designer', '2 Researchers', '1 Accessibility Expert'],
    image: 'https://images.unsplash.com/photo-1636390877494-3ba0c41c7e5e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1c2VyJTIwcmVzZWFyY2glMjBpbnRlcnZpZXd8ZW58MXx8fHwxNzU3NDk4ODAzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    summary: 'Comprehensive research study examining how voice interfaces can be designed to be more accessible for users with various disabilities, leading to new design guidelines.',
    problem: 'Voice interfaces were becoming ubiquitous, but little research existed on their accessibility for users with speech, hearing, or cognitive disabilities. Current voice UIs excluded many potential users.',
    solution: 'Conducted mixed-methods research including interviews, usability testing, and co-design sessions with 45 participants across different disability categories to develop inclusive voice UI guidelines.',
    impact: [
      'Published research paper with 500+ citations',
      'Guidelines adopted by 3 major tech companies',
      'Influenced WCAG voice interface recommendations',
      'Presented at 8 international conferences'
    ],
    process: [
      {
        title: 'Literature Review',
        description: 'Comprehensive review of existing accessibility research and voice interface studies to identify gaps.'
      },
      {
        title: 'Participant Recruitment',
        description: 'Recruited diverse participants with speech, hearing, motor, and cognitive disabilities through accessibility organizations.'
      },
      {
        title: 'Mixed Methods Study',
        description: 'Combined interviews, usability testing, and co-design sessions to understand user needs and barriers.'
      },
      {
        title: 'Guideline Development',
        description: 'Synthesized findings into actionable design guidelines for inclusive voice interfaces.'
      },
      {
        title: 'Validation & Publication',
        description: 'Validated guidelines with industry partners and published findings in academic venues.'
      }
    ],
    outcomes: [
      {
        metric: 'Research Impact',
        value: '500+',
        description: 'Academic citations of published work'
      },
      {
        metric: 'Industry Adoption',
        value: '3',
        description: 'Major tech companies using guidelines'
      },
      {
        metric: 'Conference Presentations',
        value: '8',
        description: 'International conferences presented at'
      }
    ],
    keyLearnings: [
      'Voice interfaces have unique accessibility challenges not covered by traditional guidelines',
      'Co-design with disabled users leads to more innovative solutions',
      'Academic-industry partnerships are crucial for research impact'
    ]
  }
];

export const skills = [
  'User Research',
  'Interaction Design',
  'Design Systems',
  'Prototyping',
  'Usability Testing',
  'Accessibility',
  'AI/ML Design',
  'Mobile Design',
  'Dashboard Design',
  'Voice UI',
  'Design Strategy',
  'Workshop Facilitation'
];

export const tools = [
  'Figma',
  'Principle',
  'After Effects',
  'Miro',
  'Notion',
  'Maze',
  'Hotjar',
  'Analytics',
  'Zeplin',
  'Abstract',
  'InVision',
  'Sketch'
];